import { Cell } from '@/models/GenericInterfaces'
import { Direction } from '@/models/Global'
import Message from '@/models/Message'
import { AdvancedProperty } from '@/models/Property'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { areComparable, objectsAreEqual, toComparable } from '@/utils/tools'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useTranslation } from './useTranslation'
import { useProductsStore } from '@/store/productsArchiveStore'

export const useTableSearch = () => {
  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()
  const bulkStore = useProductsBulkStore()
  const productsStore = useProductsStore()
  const { appTranslate } = useTranslation()
  const route = useRoute()

  // -------------------
  // -- Reactive vars --
  // -------------------
  const fieldSearchVars = ref<{
    search: string
    matchIndex: number
    activeColumn?: number
    mappedMatches?: number[]
  }>({
    search: '',
    matchIndex: -1,
  })

  const dataSearchVars = ref<{ currentRow: number; currentColumn: number; latestMatch?: Cell }>({ currentRow: 0, currentColumn: 0 })

  // -------------
  // -- Methods --
  // -------------

  const findHeader = (search: string, direction = Direction.forwards, includedProperties: AdvancedProperty[]) => {
    const comparableSearch = toComparable(search)

    if (comparableSearch) {
      const mappedMatches = includedProperties?.reduce((acc: number[], property, i) => {
        if (toComparable(property.defaultHeader).includes(comparableSearch) || toComparable(property.header)?.includes(comparableSearch)) {
          acc.push(i)
        }
        return acc
      }, [])

      if (mappedMatches?.length) {
        let matchIndex = 0
        if (objectsAreEqual(mappedMatches, fieldSearchVars.value.mappedMatches)) {
          const next = fieldSearchVars.value.matchIndex + direction
          matchIndex = next < 0 ? mappedMatches.length - 1 : next >= mappedMatches.length ? 0 : next
        }

        const columnIndex = mappedMatches[matchIndex]

        fieldSearchVars.value = {
          search,
          matchIndex,
          activeColumn: columnIndex,
          mappedMatches,
        }

        return columnIndex
      }
    }

    fieldSearchVars.value = {
      search,
      matchIndex: -1,
    }

    store.addMessage(
      new Message(appTranslate(`Couldn't find field matching "%s"`, [search]), appTranslate('Make sure the field is selected in the dropdown'), 'info', 5),
    )
  }

  const findData = async (
    searchString: string,
    direction: Direction = Direction.forwards,
    includedProperties: AdvancedProperty[],
    includeAllResults?: boolean,
    isRegexSearch?: boolean,
    matchCase?: boolean,
    exactMatch?: boolean,
  ) => {
    return await DataService.findProductData(
      searchString,
      route.query,
      dataSearchVars.value.currentRow,
      dataSearchVars.value.currentColumn,
      direction,
      bulkStore.combinedProperties,
      includedProperties,
      includeAllResults,
      isRegexSearch,
      matchCase,
      exactMatch,
      bulkStore.productInputBySku,
      bulkStore.extraProducts,
      bulkStore.newProducts,
      productsStore.expiredProducts,
    )
  }

  const setSearchStartingPoint = (cell?: Cell) => {
    dataSearchVars.value = {
      currentRow: cell?.rowIndex || 0,
      currentColumn: cell?.columnIndex || 0,
      latestMatch: cell,
    }
  }

  return {
    setSearchStartingPoint,
    findHeader,
    findData,
  }
}
